import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Blocks, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

const INTRO_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/r9we0mii_gemini_generated_video_1484f8c2.mp4";

export function AnalogyMachine() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [concept, setConcept] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const presets = t("atelier.analogies.presets", { returnObjects: true }) || [];

  const run = async (c) => {
    const x = ((c ?? concept) || "").trim();
    if (!x || loading) return;
    setConcept(x);
    setLoading(true); setData(null);
    try {
      const { data: res } = await api.get("/analogies", { params: { concept: x, lang: (i18n.language || "fr").slice(0, 2) } });
      setData(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.analogies.error"));
    } finally { setLoading(false); }
  };

  const deepen = () => {
    if (!data) return;
    localStorage.setItem("coccon_pending_prompt", t("atelier.analogies.deepenPrompt", { concept: data.concept }));
    navigate("/atelier");
  };

  return (
    <div data-testid="analogy-machine">
      <div className="max-w-2xl mx-auto mb-7">
        <CinematicVideo fade noSound src={INTRO_VIDEO} testId="analogy-cinematic-video" />
      </div>
      <div className="text-center mb-6">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Blocks className="text-primary pulse-star" size={22} strokeWidth={1.7} />
        </div>
        <p className="uppercase tracking-[0.25em] text-xs text-primary/80 mb-2">{t("atelier.analogies.title")}</p>
        <p className="text-foreground/55 max-w-lg mx-auto leading-relaxed text-sm">{t("atelier.analogies.subtitle")}</p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="flex flex-col sm:flex-row gap-2 mb-4 max-w-xl mx-auto">
        <input value={concept} onChange={(e) => setConcept(e.target.value)} placeholder={t("atelier.analogies.placeholder")}
          data-testid="analogy-input"
          className="flex-1 min-w-0 rounded-full bg-white/[0.03] border border-white/12 px-5 py-3 text-sm text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary/45 transition-colors" />
        <button type="submit" disabled={loading || !concept.trim()} data-testid="analogy-go"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-6 py-3 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50 flex-shrink-0">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {t("atelier.analogies.go")}
        </button>
      </form>

      {!data && !loading && (
        <div className="flex flex-wrap justify-center gap-2 mb-2" data-testid="analogy-presets">
          {presets.map((p, i) => (
            <button key={i} onClick={() => run(p)} disabled={loading}
              data-testid={`analogy-preset-${i}`}
              className="text-xs px-3.5 py-1.5 rounded-full border border-primary/20 text-foreground/65 hover:text-primary hover:border-primary/40 transition-colors">
              {p}
            </button>
          ))}
        </div>
      )}

      {loading && !data && (
        <p className="text-center font-display italic text-foreground/40 flex items-center justify-center gap-2 py-6" data-testid="analogy-loading">
          <Loader2 size={16} className="animate-spin" /> {t("atelier.analogies.loading")}
        </p>
      )}

      {data && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="mt-3" data-testid="analogy-result">
          <p className="text-center font-display text-2xl text-gradient-orange mb-5">{data.concept}</p>
          <div className="space-y-3">
            {data.analogies.map((an, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }}
                data-testid={`analogy-item-${i}`}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:border-primary/25 transition-colors">
                {an.domain && <p className="uppercase tracking-[0.2em] text-[0.7rem] text-primary/70 mb-1.5">{an.domain}</p>}
                <p className="text-sm text-foreground/80 leading-relaxed">{an.text}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-5">
            <button onClick={deepen} data-testid="analogy-deepen"
              className="btn-frost-orange inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm">
              {t("atelier.analogies.deepen")} <ArrowRight size={15} />
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
